import { formatUsPhone } from '@mevaser/shared'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Command } from 'cmdk'
import { Package, ShoppingCart, Smartphone, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { visibleGroups } from '../lib/nav.js'

interface SearchResult {
  type: 'customer' | 'order' | 'unit'
  id: string
  title: string
  subtitle: string | null
}

const RESULT_ICON = {
  customer: User,
  order: ShoppingCart,
  unit: Smartphone,
} as const

/**
 * ⌘K palette: navigation plus a live search that resolves an IMEI, an order
 * number or a phone number straight to the record. This is the feature that
 * makes the back office feel fast — one keystroke instead of page, filter,
 * scroll.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const navigate = useNavigate()
  const { can } = useAuth()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onOpenChange(!open)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onOpenChange])

  // Debounced so typing an IMEI does not fire fifteen searches.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(timer)
  }, [query])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResult[]>('/search', { query: { q: debounced } }),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 10_000,
  })

  const close = () => {
    onOpenChange(false)
    setQuery('')
  }

  const go = (to: string) => {
    close()
    void navigate({ to })
  }

  /**
   * A result opens the record itself, not the list it lives in. Landing on
   * "Customers" after searching an exact phone number means searching twice.
   * Units have no page of their own yet, so they still go to the queue.
   */
  const goToResult = (result: SearchResult) => {
    close()
    if (result.type === 'customer') {
      void navigate({ to: '/customers/$customerId', params: { customerId: result.id } })
    } else if (result.type === 'order') {
      void navigate({ to: '/orders/$orderId', params: { orderId: result.id } })
    } else {
      void navigate({ to: '/provisioning' })
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-neutral-950/40 p-4 pt-[15vh] backdrop-blur-[2px]"
      onClick={() => onOpenChange(false)}
    >
      <Command
        label="Command palette"
        shouldFilter={false}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-surface shadow-lg"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onOpenChange(false)
        }}
      >
        <Command.Input
          autoFocus
          value={query}
          onValueChange={setQuery}
          placeholder="Search an IMEI, order number, phone number, or jump to a page…"
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-foreground-subtle"
        />

        <Command.List className="max-h-80 overflow-y-auto scrollbar-thin p-1.5">
          <Command.Empty className="px-3 py-6 text-center text-sm text-foreground-muted">
            {isFetching ? 'Searching…' : 'Nothing found.'}
          </Command.Empty>

          {results.length > 0 ? (
            <Command.Group
              heading="Results"
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-foreground-subtle"
            >
              {results.map((result: SearchResult) => {
                const Icon = RESULT_ICON[result.type]
                return (
                  <Command.Item
                    key={`${result.type}-${result.id}`}
                    value={`${result.type}-${result.id}`}
                    onSelect={() => goToResult(result)}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm data-[selected=true]:bg-surface-sunken"
                  >
                    <Icon className="size-4 shrink-0 text-foreground-muted" aria-hidden="true" />
                    <span className="font-identifier">
                      {result.type === 'customer' && result.subtitle ? result.title : result.title}
                    </span>
                    {result.subtitle ? (
                      <span className="ms-auto truncate text-xs text-foreground-muted">
                        {result.type === 'customer'
                          ? formatUsPhone(result.subtitle)
                          : result.subtitle}
                      </span>
                    ) : null}
                  </Command.Item>
                )
              })}
            </Command.Group>
          ) : null}

          {visibleGroups(can).map((group) => {
            const matching = group.items.filter((item) => {
              if (!query) return true
              const haystack = [item.label, ...(item.keywords ?? [])].join(' ').toLowerCase()
              return haystack.includes(query.toLowerCase())
            })
            if (matching.length === 0) return null

            return (
              <Command.Group
                key={group.label}
                heading={group.label}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-foreground-subtle"
              >
                {matching.map((item) => (
                  <Command.Item
                    key={item.to}
                    value={`nav-${item.to}`}
                    onSelect={() => go(item.to)}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm data-[selected=true]:bg-surface-sunken"
                  >
                    <item.icon
                      className="size-4 shrink-0 text-foreground-muted"
                      aria-hidden="true"
                    />
                    {item.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )
          })}
        </Command.List>

        <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-2xs text-foreground-subtle">
          <span>
            <kbd className="rounded border border-border px-1">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="rounded border border-border px-1">↵</kbd> open
          </span>
          <span>
            <kbd className="rounded border border-border px-1">esc</kbd> close
          </span>
        </div>
      </Command>
    </div>
  )
}

export { Package }
